import { useEffect, useState } from 'react';
import { Download, Plus, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';

// Define BeforeInstallPromptEvent since it's not in standard TypeScript DOM lib
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface Window {
    deferredPrompt: BeforeInstallPromptEvent | null;
  }
}

export function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  
  useEffect(() => {
    // Detect iOS
    const isIOSDevice = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      return /iphone|ipad|ipod/.test(userAgent);
    };
    
    setIsIOS(isIOSDevice());
    
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();
      // Store the event for later use
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    
    // Listen for the appinstalled event
    const handleAppInstalled = () => {
      // Clear the deferredPrompt
      setDeferredPrompt(null);
      // Log app installed to analytics or do something else
      console.log('PWA was installed');
    };
    
    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);
  
  // Function to show install prompt
  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
      return;
    }
    
    if (!deferredPrompt) {
      console.log('Cannot show install prompt');
      return;
    }
    
    // Show the install prompt
    await deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    // The deferredPrompt can only be used once
    setDeferredPrompt(null);
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
  };
  
  // If there's no prompt and not iOS, don't show the button
  if (!deferredPrompt && !isIOS) {
    return null;
  }
  
  return (
    <>
      <button
        className="rounded-full p-2 text-gray-400 hover:bg-gray-700/50 hover:text-white"
        onClick={handleInstallClick}
        title="Open in app"
      >
        <Smartphone className="h-5 w-5" />
      </button>
      
      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 pt-16">
          <div className="relative w-full max-w-md rounded-lg bg-[#1f2333] p-6 shadow-xl">
            <button
              onClick={() => setShowIOSInstructions(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-white"
            >
              <span className="sr-only">Close</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            
            <h3 className="mb-3 text-lg font-medium text-white">Install This App on Your iOS Device</h3>
            <div className="space-y-4 text-sm text-gray-300">
              <p className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#8A6FE8] text-white text-xs">1</span>
                Tap the Share button in Safari.
              </p>
              <p className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#8A6FE8] text-white text-xs">2</span>
                Scroll down and tap "Add to Home Screen".
              </p>
              <p className="flex items-center gap-2">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#8A6FE8] text-white text-xs">3</span>
                Tap "Add" in the top right corner.
              </p>
            </div>
            
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowIOSInstructions(false)}
                className="rounded bg-[#8A6FE8] px-4 py-2 text-sm font-medium text-white hover:bg-[#7F64D3]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
} 